// Slot-based reactive system
const slot = (initialValue) => {
  let value = initialValue;
  const subscribers = [];

  return {
    get() {
      return value;
    },
    set(newValue) {
      const oldValue = value;
      value = newValue;

      // Notify all subscribers if value changed
      if (oldValue !== newValue) {
        subscribers.forEach((callback) => callback(newValue, oldValue));
      }
    },
    subscribe(callback) {
      subscribers.push(callback);
      // Return unsubscribe function
      return () => {
        const index = subscribers.indexOf(callback);
        if (index > -1) subscribers.splice(index, 1);
      };
    },
  };
};

const membership = () => {
  // Reactive slots
  const showMembershipModal = slot(false);
  const isLoggedIn = slot(false);
  const processing = slot(false);
  const submitErrorMessage = slot(null);

  return {
    // Constants
    SIGNUP_FORM_ID: "signup-form",
    SIGNUP_CTA_SELECTOR:
      'a[data-signup="true"], a[href="https://account.helmandoar.com"], a[href="https://account.helmandoar.com/"], a[href="/account/register"]',
    LOGIN_CTA_SELECTOR: 'a[data-login="true"]',
    RECOVERY_CTA_SELECTOR: 'a[data-recovery="true"]',

    // Slots
    showMembershipModal,
    isLoggedIn,
    processing,
    submitErrorMessage,

    // Regular properties
    membershipContainer: null,
    customer: null,
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    formForShow: "registration",
    currentFormStep: 1,
    emailEntered: false,
    passwordEntered: false,
    passwordConfirmed: false,
    errorMessage: null,
    showPasswordInputs: false,
    submitSuccessMessage: null,
    loginForm: null,
    passwordRecoverForm: null,
    emailIsValid: false,
    openModalTimeOut: null,
    autoPopupTimeout: null,
    scrollTrigger: 1000,
    pageTemplate: null,
    mainContainer: null,

    // Methods
    init() {
      this.mainContainer = document.querySelector("main");
      this.membershipContainer = document.querySelector(
        ".membership-modal__container"
      );
      this.loginForm = document.querySelector("#customer_login");
      this.pageTemplate = this.mainContainer.getAttribute("data-template");

      const closedCookie = this.getCookie("closedMembershipModal");
      const autoPopupCookie = this.getCookie("autoPopupShown");
      const autoPopupShown = localStorage.getItem("autoPopupShown");

      // Auto-display popup after 5 seconds if not shown before
      if (autoPopupCookie !== "true" && autoPopupShown !== "true" && closedCookie !== "true") {
        this.showPopupAfterDelay(5000);
      }

      // Find all CTA links and attach click handlers
      this.initCTAs();

      // Initialize delegated event listeners for dynamic content
      this.initDelegatedEventListeners();

      // Subscribe to processing changes to update button states
      this.processing.subscribe((isProcessing) => {
        this.updateSubmitButtons(isProcessing);
      });

      // Subscribe to submitErrorMessage changes to display errors
      this.submitErrorMessage.subscribe((errorMessage) => {
        this.displayErrorMessage(errorMessage);
      });

      // Subscribe to showMembershipModal changes
      this.showMembershipModal.subscribe((value) => {
        if (value) {
          this.makeSignUpFormVisible();
          this.showBackdrop();

          // Make the membership modal container visible
          if (this.membershipContainer) {
            this.membershipContainer.style.display = "block";
            this.membershipContainer.style.visibility = "visible";
          }
        } else {
          this.hideBackdrop();

          // Hide the signup form
          const signUpForm = document.getElementById(this.SIGNUP_FORM_ID);
          if (signUpForm) {
            signUpForm.style.display = "none";
            signUpForm.style.visibility = "hidden";
            signUpForm.classList.remove("show");
          }

          // Hide the login form
          const loginForm = document.getElementById("login-form");
          if (loginForm) {
            loginForm.style.display = "none";
            loginForm.style.visibility = "hidden";
            loginForm.classList.remove("show");
          }

          // Hide the password recovery form
          const passwordRecoverForm = document.getElementById("password_recover");
          if (passwordRecoverForm) {
            passwordRecoverForm.style.display = "none";
            passwordRecoverForm.style.visibility = "hidden";
            passwordRecoverForm.classList.remove("show");
          }

          // Hide the membership modal container
          if (this.membershipContainer) {
            this.membershipContainer.style.display = "none";
            this.membershipContainer.style.visibility = "hidden";
          }

          // Reset to step 1
          this.goToStep1();
        }
      });

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('members')) {
        this.openModal();
      }
    },
    initCTAs() {
      // Signup CTAs
      const signupLinks = document.querySelectorAll(this.SIGNUP_CTA_SELECTOR);
      signupLinks.forEach((link) => {
        link.addEventListener("click", (event) => {
          event.preventDefault();
          this.openModal();
          return false;
        });
      });

      // Login CTAs
      const loginLinks = document.querySelectorAll(this.LOGIN_CTA_SELECTOR);
      loginLinks.forEach((link) => {
        link.addEventListener("click", (event) => {
          event.preventDefault();
          this.openModal();
          this.switchForm("login");
          return false;
        });
      });

      // Recovery CTAs
      const recoveryLinks = document.querySelectorAll(
        this.RECOVERY_CTA_SELECTOR
      );
      recoveryLinks.forEach((link) => {
        link.addEventListener("click", (event) => {
          event.preventDefault();
          this.openModal();
          this.switchForm("forgot_password");
          return false;
        });
      });
    },
    initDelegatedEventListeners() {
      // Event delegation for dynamically created sign-up links
      // This allows 3rd party JS to add links with data-signup="true" attribute
      // and they will automatically trigger the modal without needing to rebind events
      document.body.addEventListener("click", (event) => {
        const signupTrigger = event.target.closest('[data-signup="true"]');
        const accountLink = event.target.closest('a[href="https://account.helmandoar.com"], a[href="https://account.helmandoar.com/"]');

        if (signupTrigger || accountLink) {
          event.preventDefault();
          if(window.isCustomerLoggedIn) {
            window.location.href = '/account';
          } else {
            this.openModal();
          }
        }
      });
    },
    updateSubmitButtons(isProcessing) {
      // Get all submit buttons in the membership forms
      const buttons = [
        document.querySelector('#signup_customer_form button[type="submit"]'),
        document.querySelector('#customer_login button[type="submit"]'),
        document.querySelector('#recover_customer_password button[type="submit"]')
      ].filter(Boolean); // Remove null/undefined buttons

      buttons.forEach((button) => {
        if (isProcessing) {
          // Store original text if not already stored
          if (!button.dataset.originalText) {
            button.dataset.originalText = button.textContent;
          }
          // Set loading state
          button.textContent = "LOADING...";
          button.disabled = true;
          button.style.opacity = "0.7";
        } else {
          // Restore original state
          if (button.dataset.originalText) {
            button.textContent = button.dataset.originalText;
            delete button.dataset.originalText;
          }
          button.disabled = false;
          button.style.opacity = "1";
        }
      });
    },
    displayErrorMessage(errorMessage) {
      // Get or create error message containers for each form
      const forms = [
        { selector: '#signup_customer_form' },
        { selector: '#customer_login' },
        { selector: '#recover_customer_password' }
      ];

      forms.forEach(({ selector }) => {
        const form = document.querySelector(selector);
        if (!form) return;

        const submitButton = form.querySelector('button[type="submit"]');
        if (!submitButton) return;

        // Get or create error container
        let errorContainer = form.querySelector('.submit-error-message');

        if (!errorContainer) {
          errorContainer = document.createElement('div');
          errorContainer.className = 'submit-error-message';
          errorContainer.style.cssText = `
            color: #ff6b6b;
            font-size: 14px;
            margin-bottom: 12px;
            padding: 8px 12px;
            background: #fff;
            border-radius: 4px;
            display: none;
          `;
          submitButton.parentNode.insertBefore(errorContainer, submitButton);
        }

        if (errorMessage) {
          errorContainer.textContent = errorMessage;
          errorContainer.style.display = 'block';
        } else {
          errorContainer.style.display = 'none';
        }
      });
    },
    makeSignUpFormVisible() {
      const signUpForm = document.getElementById(this.SIGNUP_FORM_ID);

      if (signUpForm) {
        // Make the form visible
        signUpForm.style.display = "block";
        signUpForm.style.visibility = "visible";
        signUpForm.classList.add("show");

        // Optionally focus on the email input
        const emailInput = signUpForm.querySelector("input[type='email']");
        if (emailInput) {
          window.setTimeout(() => emailInput.focus(), 200);
        }
      }
    },
    showBackdrop() {
      // Create or get existing backdrop
      let backdrop = document.getElementById("membership-modal-backdrop");

      if (!backdrop) {
        backdrop = document.createElement("div");
        backdrop.id = "membership-modal-backdrop";
        backdrop.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(30,30,30, 0.2);
          backdrop-filter: blur(5px);
          z-index: 999;
        `;
        document.body.appendChild(backdrop);

        // Close modal when backdrop is clicked
        backdrop.addEventListener("click", () => {
          this.closeModal();
        });
      }

      backdrop.style.display = "block";
    },
    hideBackdrop() {
      const backdrop = document.getElementById("membership-modal-backdrop");
      if (backdrop) {
        backdrop.style.display = "none";
      }
    },
    goToStep2() {
      // Get the signup form
      const signupForm = document.getElementById(this.SIGNUP_FORM_ID);
      if (!signupForm) return;

      // Validate email in step 1
      const emailInput = signupForm.querySelector('[data-step="1"] input[type="email"]');

      // Check email validity
      if (emailInput && !emailInput.checkValidity()) {
        emailInput.reportValidity();
        return;
      }

      // All fields are valid, proceed to step 2
      const step1 = signupForm.querySelector('[data-step="1"]');
      const step2 = signupForm.querySelector('[data-step="2"]');

      if (step1) step1.style.display = "none";
      if (step2) step2.style.display = "block";

      this.currentFormStep = 2;

      // Focus on password input
      window.setTimeout(() => {
        const passwordInput = signupForm.querySelector(
          '[data-step="2"] input[type="password"]'
        );
        if (passwordInput) passwordInput.focus();
      }, 100);
    },
    goToStep1() {
      // Get the signup form
      const signupForm = document.getElementById(this.SIGNUP_FORM_ID);
      if (!signupForm) return;

      // Show step 1, hide step 2
      const step1 = signupForm.querySelector('[data-step="1"]');
      const step2 = signupForm.querySelector('[data-step="2"]');

      if (step1) step1.style.display = "block";
      if (step2) step2.style.display = "none";

      this.currentFormStep = 1;
    },
    handleFormSubmit(event) {
      // This is called when the final "Sign Up" button is clicked
      // Store phone in sessionStorage and allow form to submit naturally
      return this.memberSignUp(event);
    },
    showPopupOnScroll() {
      const _self = this;

      const scrollThreshold = this.scrollTrigger;

      function checkScroll() {
        if (window.scrollY >= scrollThreshold) {
          _self.openModal();
          window.removeEventListener("scroll", checkScroll);
        }
      }

      window.addEventListener("scroll", checkScroll);
    },
    showPopupAfterDelay(delay) {
      const _self = this;

      // Set localStorage immediately to prevent multiple scheduled popups
      localStorage.setItem("autoPopupShown", "true");

      // Set a timeout to open the modal after the specified delay
      this.autoPopupTimeout = setTimeout(() => {
        const closedCookie = _self.getCookie("closedMembershipModal");
        if (closedCookie !== "true") {
          _self.openModal();
        }
      }, delay);
    },

    handleEmail(event) {
      this.emailIsValid = event.target.checkValidity();

      if (this.email && this.emailIsValid) {
        this.emailEntered = true;
        this.invalidEmailError = null;
      } else {
        this.invalidEmailError = "Please enter valid email address";
      }
    },
    handlePasswords() {
      if (this.password) {
        this.passwordEntered = true;
      }
    },
    handlePasswordConfirmation() {
      this.passwordConfirmed = this.password === this.confirmPassword;
    },
    checkPasswordConfirmation() {
      if (!this.passwordConfirmed) {
        this.errorMessage = "Passwords must match";
      } else {
        this.errorMessage = "";
      }
    },
    navigateNextStep() {
      this.showPasswordInputs = true;
    },
    resetForm() {
      this.email = "";
      this.password = "";
      this.passwordConfirmed = false;
      this.errorMessage = "";
      this.submitErrorMessage.set(null);
      this.emailIsValid = false;
      this.showPasswordInputs = false;
      this.confirmPassword = "";
    },
    switchForm(formType) {
      let form;
      this.formForShow = formType;
      this.resetForm();

      // Hide all forms first
      const signUpForm = document.getElementById(this.SIGNUP_FORM_ID);
      const loginForm = document.getElementById("login-form");
      const passwordRecoverForm = document.getElementById("password_recover");

      if (signUpForm) {
        signUpForm.style.display = "none";
        signUpForm.style.visibility = "hidden";
        signUpForm.classList.remove("show");
      }
      if (loginForm) {
        loginForm.style.display = "none";
        loginForm.style.visibility = "hidden";
        loginForm.classList.remove("show");
      }
      if (passwordRecoverForm) {
        passwordRecoverForm.style.display = "none";
        passwordRecoverForm.style.visibility = "hidden";
        passwordRecoverForm.classList.remove("show");
        // Reset recovery form to step 1
        this.resetRecoveryForm();
      }

      window.setTimeout(() => {
        if (formType === "registration") {
          form = signUpForm;
          if (form) {
            form.style.display = "block";
            form.style.visibility = "visible";
            form.classList.add("show");
          }
        } else if (formType === "login") {
          form = loginForm;
          if (form) {
            form.style.display = "block";
            form.style.visibility = "visible";
            form.classList.add("show");
          }
        } else if (formType === "forgot_password") {
          form = passwordRecoverForm;
          if (form) {
            form.style.display = "block";
            form.style.visibility = "visible";
            form.classList.add("show");
          }
        }

        if (form) {
          const emailInput = form.querySelector("input[type='email']");
          if (emailInput) {
            window.setTimeout(() => emailInput.focus(), 0);
          }
        }
      }, 0);
    },
    memberSignUp(event) {
      event.preventDefault();
      this.submitErrorMessage.set(null);
      this.processing.set(true);

      // Get the signup form
      const signupForm = document.getElementById("signup_customer_form");
      const formData = new FormData(signupForm);

      // Extract email and password for auto-login after successful signup
      const email = formData.get('customer[email]');
      const password = formData.get('customer[password]');
      const passwordConfirmation = formData.get('customer[password_confirmation]');

      // Validate password confirmation
      if (password !== passwordConfirmation) {
        this.submitErrorMessage.set('Passwords do not match. Please try again.');
        this.processing.set(false);
        return false;
      }

      // Submit form to worker
      fetch('https://login-worker.jonathan-efa.workers.dev', {
        method: 'POST',
        body: formData,
      })
        .then((response) => response.json())
        .then((data) => {
          if (!data.success) {
            // Check if account already exists
            if (data.error && data.error.includes('Account already exists')) {
              // Account exists - automatically login instead
              console.log('Account exists, attempting auto-login...');
              this.autoLogin(email, password);
            } else {
              // Handle other errors from worker
              this.submitErrorMessage.set(data.error || 'Registration failed. Please try again.');
              this.processing.set(false);
            }
          } else {
            // Account created successfully - auto-login
            this.autoLogin(email, password);
          }
        })
        .catch((error) => {
          console.error('Signup error:', error);
          this.submitErrorMessage.set(error.message || 'Registration failed. Please try again.');
          this.processing.set(false);
        });

      return false;
    },
    autoLogin(email, password) {
      // Fill in the hidden login form
      const loginForm = document.getElementById('customer_login');
      if (!loginForm) {
        console.error('Login form not found');
        this.processing.set(false);
        return;
      }

      const emailInput = loginForm.querySelector('input[name="customer[email]"]');
      const passwordInput = loginForm.querySelector('input[name="customer[password]"]');

      if (emailInput && passwordInput) {
        emailInput.value = email;
        passwordInput.value = password;

        // Call the existing login method
        const fakeEvent = new Event('submit', { cancelable: true });
        Object.defineProperty(fakeEvent, 'target', { value: loginForm, writable: false });
        this.memberLogIn(fakeEvent);
      } else {
        console.error('Login form inputs not found');
        this.processing.set(false);
      }
    },
    checkPendingCustomerPhone() {
      // Check if there's a pending phone update after account creation
      const pendingData = sessionStorage.getItem("pending_customer_phone");

      if (pendingData) {
        try {
          const data = JSON.parse(pendingData);

          // Only process if less than 5 minutes old
          if (Date.now() - data.timestamp < 5 * 60 * 1000) {
            // TODO: Call Cloudflare Worker to update customer phone
            // this.updateCustomerPhone(data.email, data.phone);
            console.log("Pending customer phone update:", data);
          }

          // Clear the pending data
          sessionStorage.removeItem("pending_customer_phone");
        } catch (error) {
          console.error("Error processing pending customer phone:", error);
          sessionStorage.removeItem("pending_customer_phone");
        }
      }
    },
    updateCustomerPhone(email, phone) {
      // Send phone to Cloudflare Worker to update the customer account
      fetch("YOUR_CLOUDFLARE_WORKER_URL", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          phone: phone,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          console.log("Phone updated successfully", data);
        })
        .catch((error) => {
          console.error("Error updating phone:", error);
        })
        .finally(() => {
          // Reload regardless of worker success/failure
          setTimeout(() => {
            this.showMembershipModal.set(false);
            window.location.reload();
          }, 1500);
        });
    },
    memberLogIn(event) {

      event.preventDefault();
      this.submitErrorMessage.set(null);
      this.processing.set(true);
      const formData = new FormData(this.loginForm);

      fetch(this.loginForm.action, {
        method: this.loginForm.method || "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Error logging in. Please try again.");
          }
          return response.text();
        })
        .then((data) => {
          const container = document.createElement("div");
          container.innerHTML = data;

          if (container.querySelector(".errors li")) {
            const errorText = container.querySelector(
              ".errors li"
            ).innerText;
            this.submitErrorMessage.set(errorText || 'Failed to log in. Please try again.');
            this.isLoggedIn.set(false);
          } else {
            this.isLoggedIn.set(true);
            this.submitErrorMessage.set(null);
            this.showMembershipModal.set(!this.isLoggedIn.get());
          }
          if (this.isLoggedIn.get()) {
            window.location.reload();
          }
          this.processing.set(false);
        })
        .catch((error) => {
          this.processing.set(false);
          console.error("Error:", error);
        });
    },
    recoverPassword(event) {
      event.preventDefault();
      this.submitErrorMessage.set(null);
      this.processing.set(true);

      const recoverForm = document.querySelector("#recover_customer_password");
      if (!recoverForm) {
        console.error('Password recovery form not found');
        this.processing.set(false);
        return false;
      }

      const formData = new FormData(recoverForm);

      fetch(recoverForm.action, {
        method: recoverForm.method || "POST",
        body: formData,
      })
        .then(() => {
          // Shopify recovery endpoint redirects in any case
          this.processing.set(false);
          this.submitErrorMessage.set(null);

          // Show success message step
          this.goToRecoveryStep2();
        })
        .catch((error) => {
          this.processing.set(false);
          console.error("Error:", error);
          this.submitErrorMessage.set(
            error.message || "Failed to send reset email. Please try again."
          );
        });

      return false;
    },
    goToRecoveryStep2() {
      // Hide step 1 (email input), show step 2 (success message)
      const recoverForm = document.querySelector("#password_recover");
      if (!recoverForm) return;

      const step1 = recoverForm.querySelector('[data-step="1"]');
      const step2 = recoverForm.querySelector('[data-step="2"]');

      if (step1) step1.style.display = "none";
      if (step2) step2.style.display = "block";
    },
    resetRecoveryForm() {
      // Reset recovery form back to step 1
      const recoverForm = document.querySelector("#password_recover");
      if (!recoverForm) return;

      const step1 = recoverForm.querySelector('[data-step="1"]');
      const step2 = recoverForm.querySelector('[data-step="2"]');

      if (step1) step1.style.display = "block";
      if (step2) step2.style.display = "none";
    },
    // Alias for backward compatibility
    forgotPassword(event) {
      this.recoverPassword(event);
    },
    closeModal() {
      this.showMembershipModal.set(false);
      this.resetForm();
      this.setCookie("closedMembershipModal", true, 7);
    },
    openModal() {
      if(document.querySelector('.site-header.active')) {
        document.querySelector('[data-header-overlay]')?.click();
      }

      // Close cart if visible
      const closeCartButton = document.querySelector('div.upcart-header-close-button');
      if (closeCartButton) {
        closeCartButton.click();
      }
      const closeCartButton2 = document.querySelector('.upcart-public-header__close-button');
      if (closeCartButton2) {
        closeCartButton2.click();
      }

      if(window.isCustomerLoggedIn) {
        window.location.href = '/account';
        return;
      }


      if (this.openModalTimeOut) {
        clearTimeout(this.openModalTimeOut);
      }

      // Clear auto-popup timeout if it's still pending
      if (this.autoPopupTimeout) {
        clearTimeout(this.autoPopupTimeout);
      }

      // Set the auto-popup localStorage when modal is opened (by any trigger)
      localStorage.setItem("autoPopupShown", "true");

      this.showMembershipModal.set(true);

      // Focus on email input after modal opens
      window.setTimeout(() => {
        const form = document.getElementById(this.SIGNUP_FORM_ID);
        if (form) {
          const emailInput = form.querySelector("input[type='email']");
          if (emailInput) {
            emailInput.focus();
          }
        }
      }, 200);
    },
    setCookie(name, value, days) {
      var expires = "";
      if (days) {
        var date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
      }
      document.cookie = name + "=" + (value || "") + expires + "; path=/";
    },
    getCookie(name) {
      var nameEQ = name + "=";
      var ca = document.cookie.split(";");
      for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == " ") c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    },
    eraseCookie(name) {
      document.cookie =
        name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    },
  };
};

console.log("membership.JS loaded v1");

window.$membership = membership();

// Initialize when DOM is fully loaded, but only if customer is NOT logged in
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.$membership.init();
  });
} else {
  // DOM is already loaded
  window.$membership.init();
}
